import React, { useState } from 'react';
import {
    SaveButton,
    Toolbar,
    useTranslate,
    useNotify,
    useRedirect,
    useRecordContext,
    useDataProvider,
    useUpdate,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import {
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Alert,
    TextField,
} from '@mui/material';
import { Save, Schedule } from '@mui/icons-material';
import { CustomDateTimeInput } from './CustomDateTimeInput';
import { ValidatedSaveButton } from './ValidatedSaveButton';

export const SaveOptionsToolbar = (props: any) => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const form = useFormContext();
    const isFormValid = form.formState.isValid;

    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    const [effectiveDate, setEffectiveDate] = useState('');
    const [reason, setReason] = useState('');
    const [isScheduling, setIsScheduling] = useState(false);

    const getDefaultEffectiveDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        return tomorrow.toISOString();
    };

    const openScheduleDialog = () => {
        setShowScheduleDialog(true);
        setEffectiveDate(getDefaultEffectiveDate());
        setReason('');
    };

    const handleScheduleChange = async () => {
        if (!effectiveDate) {
            notify('resources.fee-templates.validation.effectiveDateRequired', { type: 'warning' });
            return;
        }

        if (!record) {
            notify('Error: No template record found', { type: 'error' });
            return;
        }

        setIsScheduling(true);
        
        try {
            // Get the current form values using React Admin's useFormContext hook
            const formValues = form.getValues();
            console.log('Current form values:', formValues);
            const feeStructure = formValues.feeStructure;
            
            if (!feeStructure) {
                console.error('Fee structure not found in form values:', formValues);
                throw new Error('Fee structure not found');
            }
            
            console.log('Fee structure to be scheduled:', feeStructure);

            // Create a scheduled change
            await dataProvider.create('fee-template-changes', {
                data: {
                    templateId: record.id, // Send the actual ID (string)
                    newStructure: feeStructure,
                    effectiveDate: effectiveDate,
                    reason: reason || null,
                }
            });
            
            notify('resources.fee-templates.notifications.changeScheduled', { type: 'success' });
            redirect('/fee-templates');
        } catch (error: any) {
            console.error('Failed to schedule change:', error);
            // Show the actual error message from the API if available
            const errorMessage = error?.body?.error || error?.message || 'resources.fee-templates.notifications.scheduleFailed';
            notify(errorMessage, { type: 'error' });
        } finally {
            setIsScheduling(false);
            setShowScheduleDialog(false);
        }
    };

    return (
        <>
            <Toolbar 
                {...props}
                sx={{ 
                    backgroundColor: '#FAFAFA',
                    borderTop: '1px solid #E0E0E0',
                    px: 3,
                    py: 2,
                    mt: 4,
                    '& .MuiButton-root': {
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1.5,
                    },
                }}
            >
                <ValidatedSaveButton
                    variant="contained"
                    sx={{
                        backgroundColor: '#00C853',
                        mr: 2,
                        '&:hover': {
                            backgroundColor: '#00A843',
                            boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                        },
                    }}
                    label={translate('resources.fee-templates.actions.saveNow')}
                    icon={<Save />}
                />
                <Button
                    onClick={openScheduleDialog}
                    variant="contained"
                    startIcon={<Schedule />}
                    disabled={!isFormValid}
                    sx={{
                        backgroundColor: '#00C853',
                        '&:hover': {
                            backgroundColor: '#00A843',
                            boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                        },
                    }}
                >
                    {translate('resources.fee-templates.actions.scheduleChange')}
                </Button>
            </Toolbar>

            {/* Schedule Change Dialog */}
            <Dialog 
                open={showScheduleDialog} 
                onClose={() => setShowScheduleDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule sx={{ color: '#00C853' }} />
                        {translate('resources.fee-templates.schedule.title')}
                    </Typography>
                </DialogTitle>
                
                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                            {translate('resources.fee-templates.schedule.info')}
                        </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <CustomDateTimeInput
                            source="effectiveDate"
                            label={translate('resources.fee-templates.schedule.effectiveDate')}
                            value={effectiveDate}
                            onChange={setEffectiveDate}
                            helperText={translate('resources.fee-templates.schedule.effectiveDateHelper')}
                            fullWidth
                        />

                        <TextField
                            label={translate('resources.fee-templates.schedule.reason')}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            multiline
                            rows={3}
                            fullWidth
                            helperText={translate('resources.fee-templates.schedule.reasonHelper')}
                        />
                    </Box>

                    <Alert severity="warning" sx={{ mt: 3 }}>
                        <Typography variant="body2">
                            {translate('resources.fee-templates.schedule.warning')}
                        </Typography>
                    </Alert>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button 
                        onClick={() => setShowScheduleDialog(false)}
                        color="inherit"
                    >
                        {translate('ra.action.cancel')}
                    </Button>
                    <Button 
                        onClick={handleScheduleChange}
                        variant="contained"
                        startIcon={<Schedule />}
                        disabled={isScheduling}
                        sx={{
                            backgroundColor: '#00C853',
                            '&:hover': {
                                backgroundColor: '#00A843',
                            },
                        }}
                    >
                        {isScheduling 
                            ? translate('ra.message.loading')
                            : translate('resources.fee-templates.actions.confirmSchedule')
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};